import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type VisibilityState,
} from '@tanstack/react-table';
import { ArrowUpDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from './StatusBadge';
import { formatDate, truncate, getSourceLabel } from '@/lib/utils';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import type { Question } from '@/types';

interface QuestionsTableProps {
  data: Question[];
  isLoading?: boolean;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort?: (column: string) => void;
}

export function QuestionsTable({
  data,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
}: QuestionsTableProps) {
  const navigate = useNavigate();
  const { isMobile, isDesktop } = useBreakpoint();

  const columns = useMemo<ColumnDef<Question>[]>(
    () => [
      {
        accessorKey: 'question_text',
        header: 'Question',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="max-w-[400px] lg:max-w-[400px] md:max-w-[250px]">
            <div className="font-medium">
              {truncate(row.original.title || row.original.question_text, 80)}
            </div>
            {row.original.title && (
              <div className="text-sm text-muted-foreground">
                {truncate(row.original.question_text, 60)}
              </div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: () => (
          <Button variant="ghost" onClick={() => onSort?.('status')} className="-ml-4">
            Status <ArrowUpDown className="ml-1 h-4 w-4 opacity-50" />
          </Button>
        ),
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        accessorKey: 'source',
        header: () => (
          <Button variant="ghost" onClick={() => onSort?.('source')} className="-ml-4">
            Source <ArrowUpDown className="ml-1 h-4 w-4 opacity-50" />
          </Button>
        ),
        cell: ({ row }) => (
          <span className="text-sm">
            {getSourceLabel(row.original.source || '')}
          </span>
        ),
      },
      {
        accessorKey: 'asker_name',
        header: () => (
          <Button variant="ghost" onClick={() => onSort?.('asker_name')} className="-ml-4">
            Asker <ArrowUpDown className="ml-1 h-4 w-4 opacity-50" />
          </Button>
        ),
        cell: ({ row }) => (
          <span className="text-sm">{row.original.asker_name || '-'}</span>
        ),
      },
      {
        accessorKey: 'answered_by',
        header: 'Answered By',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.answered_by?.doctor_name || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'timestamp',
        header: () => (
          <Button variant="ghost" onClick={() => onSort?.('timestamp')} className="-ml-4">
            Date <ArrowUpDown className="ml-1 h-4 w-4 opacity-50" />
          </Button>
        ),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {formatDate(row.original.timestamp)}
          </span>
        ),
      },
    ],
    []
  );

  const columnVisibility = useMemo<VisibilityState>(
    () =>
      isDesktop
        ? { source: true, answered_by: true }
        : { source: false, answered_by: false },
    [isDesktop]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    state: {
      sorting: [{ id: sortBy, desc: sortOrder === 'desc' }],
      columnVisibility,
    },
  });

  const handleRowClick = (question: Question) => {
    navigate(`/questions/${question._id}`);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) =>
          isMobile ? (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ) : (
            <Skeleton key={i} className="h-16 w-full" />
          )
        )}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        No questions found
      </div>
    );
  }

  // Mobile: card layout
  if (isMobile) {
    return (
      <div className="space-y-3">
        {data.map((question) => (
          <div
            key={question._id}
            onClick={() => handleRowClick(question)}
            className="cursor-pointer rounded-lg border p-4 transition-colors hover:bg-accent/50 active:bg-accent"
          >
            <p className="line-clamp-2 font-medium">
              {question.title || question.question_text}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <StatusBadge status={question.status} />
              {question.source && (
                <span>{getSourceLabel(question.source)}</span>
              )}
              <span>{formatDate(question.timestamp)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Tablet/Desktop: table layout
  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow
            key={row.id}
            onClick={() => handleRowClick(row.original)}
            className="cursor-pointer"
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
